import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Spinner from "../../../shared/Spinner";
import { useGetClientsQuery } from "../../../redux/services/clients/clientsApi";
import { useGetJobsQuery } from "../../../redux/services/jobs/jobsApi";
import {
  useGetInvoiceV2Query,
  useUpdateInvoiceV2Mutation,
} from "../../../redux/services/invoices/invoicesV2Api";
import InvoiceV2Editor from "../components/InvoiceV2Editor";
import { InvoiceTagsInline } from "../../../components";
import {
  buildTermsState,
  buildInvoiceV2Payload,
  buildTotals,
  createLineItem,
  createLineItemFromJob,
  createTotalsAdjustments,
  DEFAULT_TAX_SETTINGS,
  extractJobs,
  hydrateCredits,
  hydrateDiscounts,
  hydrateFees,
  hydrateLineItems,
  hydrateTaxSettings,
  hydrateTerms,
} from "../utils";

const InvoiceV2Edit = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { data: invoice, isLoading, isError, error } = useGetInvoiceV2Query(
    { id: params.id },
    { skip: !params.id }
  );
  const { data: clientsData, isLoading: clientsLoading } = useGetClientsQuery({
    limit: 100,
    sortBy: "name",
    sortOrder: "asc",
  });
  const [updateInvoiceV2, { isLoading: updating }] = useUpdateInvoiceV2Mutation();

  const [client, setClient] = useState(null);
  const [status, setStatus] = useState("draft");
  const [terms, setTerms] = useState("");
  const [termsCode, setTermsCode] = useState("custom");
  const [netDays, setNetDays] = useState(null);
  const [paymentInstructions, setPaymentInstructions] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [taxSettings, setTaxSettings] = useState(DEFAULT_TAX_SETTINGS);
  const [lineItems, setLineItems] = useState([createLineItem("manual")]);
  const [discounts, setDiscounts] = useState([]);
  const [fees, setFees] = useState([]);
  const [credits, setCredits] = useState([]);
  const [adjustments, setAdjustments] = useState(createTotalsAdjustments());

  useEffect(() => {
    if (!invoice?._id) {
      return;
    }

    setClient(invoice.client || null);
    setStatus(invoice.status || "draft");
    const termsState = hydrateTerms(invoice);
    setTerms(termsState.terms);
    setTermsCode(termsState.termsCode);
    setNetDays(termsState.netDays);
    setPaymentInstructions(termsState.paymentInstructions);
    setDueDate(termsState.dueDate);
    setTaxSettings(hydrateTaxSettings(invoice));
    setLineItems(hydrateLineItems(invoice));
    setDiscounts(hydrateDiscounts(invoice));
    setFees(hydrateFees(invoice));
    setCredits(hydrateCredits(invoice));
    setAdjustments(createTotalsAdjustments(invoice.totals || {}));
  }, [invoice]);

  const { data: jobsData, isLoading: jobsLoading } = useGetJobsQuery(
    {
      page: 1,
      limit: 100,
      client: client?._id,
      sortBy: "transactionDate",
      sortOrder: "desc",
    },
    { skip: !client?._id }
  );
  const jobs = useMemo(() => extractJobs(jobsData), [jobsData]);
  const totals = useMemo(
    () => buildTotals(lineItems, adjustments, discounts, fees, credits),
    [adjustments, credits, discounts, fees, lineItems]
  );

  function syncTerms(updates = {}) {
    const nextTerms = buildTermsState({
      terms,
      termsCode,
      netDays,
      paymentInstructions,
      dueDate: updates.dueDate !== undefined ? updates.dueDate : dueDate,
      ...updates,
    });

    setTerms(nextTerms.terms);
    setTermsCode(nextTerms.termsCode);
    setNetDays(nextTerms.netDays);
    setPaymentInstructions(nextTerms.paymentInstructions);
    setDueDate(nextTerms.dueDate);
  }

  function updateLineItem(localId, updates) {
    setLineItems((current) =>
      current.map((lineItem) =>
        lineItem.localId === localId ? { ...lineItem, ...updates } : lineItem
      )
    );
  }

  function removeLineItem(localId) {
    setLineItems((current) => current.filter((lineItem) => lineItem.localId !== localId));
  }

  async function handleSubmit() {
    const payload = buildInvoiceV2Payload({
      client,
      status,
      terms,
      termsCode,
      netDays,
      paymentInstructions,
      dueDate,
      taxSettings,
      lineItems,
      discounts,
      fees,
      credits,
      totals,
    });

    try {
      const response = await updateInvoiceV2({
        id: params.id,
        body: payload,
      }).unwrap();
      toast.success(response.message || "Updated Invoice V2", {
        toastId: "invoice-v2-updated",
      });
      navigate(`/invoices/v2/${response.data._id}`);
    } catch (submitError) {
      toast.error(submitError?.data?.message || "Unable to update invoice", {
        toastId: "invoice-v2-update-error",
      });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 px-4 py-6 md:px-6">
        <Spinner size="md" />
        <span className="text-sm text-gray-600">Loading invoice...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4 px-4 py-6 md:px-6">
        <h1 className="text-2xl font-semibold text-gray-900">Edit Invoice V2</h1>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error?.data?.message || "Unable to load this invoice."}
        </div>
      </div>
    );
  }

  if (!["draft", "review"].includes(invoice?.status)) {
    return (
      <div className="space-y-4 px-4 py-6 md:px-6">
        <h1 className="text-2xl font-semibold text-gray-900">Edit Invoice V2</h1>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
          Only draft and review invoices are directly editable in this rollout phase.
        </div>
      </div>
    );
  }

  return (
    <InvoiceV2Editor
      title={invoice?.invoiceNumber || "Edit Invoice V2"}
      subtitle="Adjust draft and review invoices without touching the legacy invoice flow."
      titleInlineExtras={
        invoice?._id ? (
          <InvoiceTagsInline invoice={invoice} editable badgeSize="lg" />
        ) : null
      }
      clientsData={clientsData}
      clientsLoading={clientsLoading}
      jobs={jobs}
      jobsLoading={jobsLoading}
      client={client}
      onClientChange={setClient}
      clientLocked
      status={status}
      onStatusChange={setStatus}
      terms={terms}
      onTermsChange={(value) => syncTerms({ terms: value })}
      termsCode={termsCode}
      onTermsCodeChange={(value) => syncTerms({ termsCode: value, dueDate: "" })}
      netDays={netDays}
      onNetDaysChange={(value) => syncTerms({ netDays: value, dueDate: "" })}
      paymentInstructions={paymentInstructions}
      onPaymentInstructionsChange={(value) => syncTerms({ paymentInstructions: value })}
      dueDate={dueDate}
      onDueDateChange={setDueDate}
      taxSettings={taxSettings}
      onTaxSettingsChange={setTaxSettings}
      lineItems={lineItems}
      onLineItemChange={updateLineItem}
      onLineItemRemove={removeLineItem}
      onAddManualLine={() =>
        setLineItems((current) => [...current, createLineItem("manual", taxSettings)])
      }
      onAddJobLines={(selectedJobs) =>
        setLineItems((current) => [
          ...current,
          ...selectedJobs.map((job) => createLineItemFromJob(job, taxSettings)),
        ])
      }
      discounts={discounts}
      onDiscountsChange={setDiscounts}
      fees={fees}
      onFeesChange={setFees}
      credits={credits}
      onCreditsChange={setCredits}
      totals={totals}
      submitLabel="Update Invoice V2"
      onSubmit={handleSubmit}
      submitting={updating}
      onBack={() => navigate(`/invoices/v2/${params.id}`)}
    />
  );
};

export default InvoiceV2Edit;
